import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
  writeBatch,
  runTransaction,
} from "firebase/firestore";

import { db } from "../../firebase";

import RegistrationCardModal from "./RegistrationCardModal";
import InvoiceModal from "./InvoiceModal";
import GuestDetailsForm from "./GuestDetailsForm";

import {
  saveGuestProfiles,
  uploadGuestFile,
} from "../../services/guestProfileService";

import {
  calculateBill,
  formatMoney,
  toAmount,
} from "../../services/billingService";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const lower = (value) =>
  clean(value).toLowerCase();

const normalizePhone = (value) =>
  String(value ?? "").replace(/\D/g, "");

const normalizeIdNumber = (value) =>
  clean(value)
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();

const roundMoney = (value) =>
  Math.round(
    (toAmount(value) + Number.EPSILON) *
      100
  ) / 100;

const getDateObject = (value) => {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (value?.seconds) {
    return new Date(value.seconds * 1000);
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date;
};

const getTimestamp = (value) =>
  getDateObject(value)?.getTime() || 0;

const formatDateTime = (value) => {
  const date = getDateObject(value);

  if (!date) return "-";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const maskIdNumber = (value) => {
  const idNumber = normalizeIdNumber(value);

  if (!idNumber) return "-";
  if (idNumber.length <= 4) return idNumber;

  return `${"X".repeat(
    Math.max(4, idNumber.length - 4)
  )}${idNumber.slice(-4)}`;
};

const getPrimaryGuest = (stay = {}) => {
  const guests = Array.isArray(stay.guests)
    ? stay.guests
    : [];

  if (guests.length > 0) {
    return (
      guests.find(
        (guest) => guest?.isPrimary === true
      ) ||
      guests[0]
    );
  }

  return {
    profileId:
      stay.primaryGuestProfileId || "",

    name:
      stay.guestName ||
      stay.name ||
      "Guest",

    phoneNumber:
      stay.phoneNumber ||
      stay.phone ||
      "",

    address: stay.address || "",

    age: stay.age || "",

    gender: stay.gender || "",

    nationality:
      stay.nationality || "Indian",

    idType: stay.idType || "",

    idNumber: stay.idNumber || "",

    idFrontUrl:
      stay.idFrontUrl || "",

    idBackUrl:
      stay.idBackUrl || "",
  };
};

const getGuestTag = (
  stay = {},
  primaryGuest = {}
) => {
  if (
    stay.guestTag === "blacklist" ||
    primaryGuest.isBlacklisted === true
  ) {
    return "blacklist";
  }

  if (
    stay.guestTag === "vip" ||
    primaryGuest.isVip === true
  ) {
    return "vip";
  }

  return "normal";
};

const getGuestGroupKey = (
  stay,
  primaryGuest
) => {
  const profileId = clean(
    primaryGuest.profileId ||
      stay.primaryGuestProfileId
  );

  if (profileId) {
    return `PROFILE_${profileId}`;
  }

  const phoneNumber = normalizePhone(
    primaryGuest.phoneNumber ||
      stay.phoneNumber
  );

  if (phoneNumber) {
    return `PHONE_${phoneNumber}`;
  }

  const idNumber = normalizeIdNumber(
    primaryGuest.idNumber ||
      stay.idNumber
  );

  if (idNumber) {
    return `ID_${idNumber}`;
  }

  return `STAY_${stay.id}`;
};

const calculateStayBill = (stay = {}) =>
  calculateBill(stay, {
    discountAmount:
      stay.discountAmount,

    gstRate: stay.gstRate,
  });

const getStayCheckoutDate = (stay) =>
  stay.checkedOutAt ||
  stay.actualCheckOutAt ||
  stay.checkOutAt ||
  "";

const getStayStatus = (stay) => {
  const status = clean(stay?.status).toLowerCase();

  if (
    status === "checked_out" ||
    status === "checkout" ||
    stay?.checkedOut === true
  ) {
    return "checked_out";
  }

  if (
    status === "cancelled" ||
    status === "canceled"
  ) {
    return "cancelled";
  }

  return "checked_in";
};

const buildGuestSearchText = (guest) =>
  [
    guest.guestName,
    guest.phoneNumber,
    guest.profileId,
    guest.idNumber,
    guest.address,

    ...guest.stays.flatMap((stay) => [
      stay.room,
      stay.adRegisterSerialNo,
      stay.bookingSource,
      stay.idNumber,
      stay.otaBookingId,
    ]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

/* -------------------------------------------------------------------------- */
/*                            Guest History Panel                             */
/* -------------------------------------------------------------------------- */

function GuestHistoryPanel({
  hotelId,
  hotelName,
}) {
  const [stays, setStays] =
    useState([]);

  const [
    guestProfiles,
    setGuestProfiles,
  ] = useState([]);

  const [loading, setLoading] =
    useState(true);

  const [loadError, setLoadError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all");

  const [sortBy, setSortBy] =
    useState("recent");

  const [
    selectedGuestKey,
    setSelectedGuestKey,
  ] = useState("");

  const [
    selectedRegistrationStay,
    setSelectedRegistrationStay,
  ] = useState(null);

  const [
    selectedInvoiceStay,
    setSelectedInvoiceStay,
  ] = useState(null);

  const [
    savingGuestKey,
    setSavingGuestKey,
  ] = useState("");

  const [
    editGuestOpen,
    setEditGuestOpen,
  ] = useState(false);

  const [
    addGuestOpen,
    setAddGuestOpen,
  ] = useState(false);

  const [
    guestFormSaving,
    setGuestFormSaving,
  ] = useState(false);

  const [
    editGuestForm,
    setEditGuestForm,
  ] = useState({
    name: "",
    phoneNumber: "",
    age: "",
    gender: "",
    nationality: "Indian",
    address: "",
    idType: "",
    idNumber: "",

    // CRM edit: existing ID images + optional replacement files
    idFrontUrl: "",
    idBackUrl: "",
    idFrontFile: null,
    idBackFile: null,
  });

  const [
    addGuestForm,
    setAddGuestForm,
  ] = useState({
    profileId: "",
    searchTerm: "",
    name: "",
    phoneNumber: "",
    age: "",
    gender: "",
    nationality: "Indian",
    address: "",
    relation: "Primary",
    idType: "Aadhaar Card",
    idNumber: "",
    idFrontFile: null,
    idBackFile: null,
    idFrontUrl: "",
    idBackUrl: "",
    sameAddressAsPrimary: false,
    visitCount: 0,
    isVip: false,
    isBlacklisted: false,
    blacklistReason: "",
    isPrimary: true,
  });

  /* ------------------------------------------------------------------------ */
  /*                           Firestore Listener                              */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!hotelId) {
      setStays([]);
      setLoading(false);
      setLoadError("Hotel ID missing hai.");

      return undefined;
    }

    setLoading(true);
    setLoadError("");

    const staysQuery = query(
      collection(db, "hotelStays"),
      where("hotelId", "==", hotelId)
    );

    return onSnapshot(
      staysQuery,

      (snapshot) => {
        const list = snapshot.docs
          .map((stayDocument) => ({
            id: stayDocument.id,
            stayId: stayDocument.id,
            ...stayDocument.data(),
          }))
          .sort(
            (firstStay, secondStay) =>
              getTimestamp(
                secondStay.checkInAt
              ) -
              getTimestamp(
                firstStay.checkInAt
              )
          );

        setStays(list);
        setLoading(false);
        setLoadError("");
      },

      (error) => {
        console.error(
          "Guest history load error:",
          error
        );

        setStays([]);
        setLoading(false);

        setLoadError(
          error?.message ||
            "Guest history load nahi hui."
        );
      }
    );
  }, [hotelId]);

  useEffect(() => {
    if (!hotelId) {
      setGuestProfiles([]);
      return undefined;
    }

    const profilesQuery = query(
      collection(db, "guestProfiles"),
      where("hotelId", "==", hotelId)
    );

    return onSnapshot(
      profilesQuery,
      (snapshot) => {
        setGuestProfiles(
          snapshot.docs.map(
            (profileDocument) => ({
              id: profileDocument.id,
              profileId:
                profileDocument.id,
              ...profileDocument.data(),
            })
          )
        );
      },
      (error) => {
        console.error(
          "Guest profiles load error:",
          error
        );
      }
    );
  }, [hotelId]);

  /* ------------------------------------------------------------------------ */
  /*                             Guest Grouping                                */
  /* ------------------------------------------------------------------------ */

  const guestGroups = useMemo(() => {
    const groups = {};

    stays.forEach((stay) => {
      const primaryGuest =
        getPrimaryGuest(stay);

      const groupKey =
        getGuestGroupKey(
          stay,
          primaryGuest
        );

      const bill =
        calculateStayBill(stay);

      const checkInTimestamp =
        getTimestamp(stay.checkInAt);

      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,

          profileId: clean(
            primaryGuest.profileId ||
              stay.primaryGuestProfileId
          ),

          guestName:
            clean(
              primaryGuest.name ||
                stay.guestName
            ) || "Guest",

          phoneNumber:
            normalizePhone(
              primaryGuest.phoneNumber ||
                stay.phoneNumber
            ),

          address: clean(
            primaryGuest.address ||
              stay.address
          ),

          age:
            primaryGuest.age ||
            stay.age ||
            "",

          gender: clean(
            primaryGuest.gender ||
              stay.gender
          ),

          nationality:
            clean(
              primaryGuest.nationality ||
                stay.nationality
            ) || "Indian",

          idType: clean(
            primaryGuest.idType ||
              stay.idType
          ),

          idNumber:
            normalizeIdNumber(
              primaryGuest.idNumber ||
                stay.idNumber
            ),

          idFrontUrl: clean(
            primaryGuest.idFrontUrl ||
              stay.idFrontUrl
          ),

          idBackUrl: clean(
            primaryGuest.idBackUrl ||
              stay.idBackUrl
          ),

          tag: getGuestTag(
            stay,
            primaryGuest
          ),

          blacklistReason: clean(
            primaryGuest.blacklistReason ||
              stay.blacklistReason
          ),

          stays: [],

          totalSpend: 0,

          totalPaid: 0,

          pending: 0,

          totalDiscount: 0,

          totalFood: 0,

          totalLaundry: 0,

          totalExtra: 0,

          lastStayAt:
            stay.checkInAt || "",

          lastStayTimestamp:
            checkInTimestamp,

          firstStayAt:
            stay.checkInAt || "",

          firstStayTimestamp:
            checkInTimestamp,

          lastRoom:
            stay.room || "",

          currentStayCount: 0,

          completedStayCount: 0,
        };
      }

      const group = groups[groupKey];

      group.stays.push({
        ...stay,
        calculatedBill: bill,
      });

      group.totalSpend = roundMoney(
        group.totalSpend +
          bill.grandTotal
      );

      group.totalPaid = roundMoney(
        group.totalPaid +
          bill.paid
      );

      group.pending = roundMoney(
        group.pending +
          bill.balance
      );

      group.totalDiscount = roundMoney(
        group.totalDiscount +
          bill.discount
      );

      group.totalFood = roundMoney(
        group.totalFood +
          bill.food
      );

      group.totalLaundry = roundMoney(
        group.totalLaundry +
          bill.laundry
      );

      group.totalExtra = roundMoney(
        group.totalExtra +
          bill.extra
      );

      if (
        getStayStatus(stay) ===
        "checked_out"
      ) {
        group.completedStayCount += 1;
      } else {
        group.currentStayCount += 1;
      }

      if (
        checkInTimestamp >
        group.lastStayTimestamp
      ) {
        group.lastStayTimestamp =
          checkInTimestamp;

        group.lastStayAt =
          stay.checkInAt || "";

        group.lastRoom =
          stay.room || "";

        group.guestName =
          clean(
            primaryGuest.name ||
              stay.guestName
          ) || group.guestName;

        group.phoneNumber =
          normalizePhone(
            primaryGuest.phoneNumber ||
              stay.phoneNumber
          ) || group.phoneNumber;

        group.address =
          clean(
            primaryGuest.address ||
              stay.address
          ) || group.address;

        group.idFrontUrl =
          clean(
            primaryGuest.idFrontUrl ||
              stay.idFrontUrl
          ) || group.idFrontUrl;

        group.idBackUrl =
          clean(
            primaryGuest.idBackUrl ||
              stay.idBackUrl
          ) || group.idBackUrl;

        group.tag = getGuestTag(
          stay,
          primaryGuest
        );

        group.blacklistReason =
          clean(
            primaryGuest.blacklistReason ||
              stay.blacklistReason
          );
      }

      if (
        checkInTimestamp <
        group.firstStayTimestamp
      ) {
        group.firstStayTimestamp =
          checkInTimestamp;

        group.firstStayAt =
          stay.checkInAt || "";
      }
    });

    /*
     * Standalone CRM profiles:
     * Guest ko check-in/stay ke bina bhi CRM me add kiya ja sakta hai.
     * Agar profile ka hotelStays record nahi hai to bhi card dikhega.
     */
    guestProfiles.forEach(
      (profile) => {
        const profileId =
          clean(
            profile.profileId ||
              profile.id
          );

        const phoneNumber =
          normalizePhone(
            profile.phoneNumber
          );

        const idNumber =
          normalizeIdNumber(
            profile.idNumber
          );

        const existingKey =
          profileId &&
          groups[
            `PROFILE_${profileId}`
          ]
            ? `PROFILE_${profileId}`
            : phoneNumber &&
              groups[
                `PHONE_${phoneNumber}`
              ]
            ? `PHONE_${phoneNumber}`
            : idNumber &&
              groups[
                `ID_${idNumber}`
              ]
            ? `ID_${idNumber}`
            : "";

        if (existingKey) {
          const group =
            groups[existingKey];

          group.profileId =
            group.profileId ||
            profileId;

          group.guestName =
            clean(profile.name) ||
            group.guestName;

          group.phoneNumber =
            phoneNumber ||
            group.phoneNumber;

          group.address =
            clean(profile.address) ||
            group.address;

          group.age =
            profile.age ||
            group.age;

          group.gender =
            clean(profile.gender) ||
            group.gender;

          group.nationality =
            clean(
              profile.nationality
            ) ||
            group.nationality;

          group.idType =
            clean(profile.idType) ||
            group.idType;

          group.idNumber =
            idNumber ||
            group.idNumber;

          group.idFrontUrl =
            clean(
              profile.idFrontUrl
            ) ||
            group.idFrontUrl;

          group.idBackUrl =
            clean(
              profile.idBackUrl
            ) ||
            group.idBackUrl;

          if (
            profile.isBlacklisted ===
            true
          ) {
            group.tag =
              "blacklist";
          } else if (
            profile.isVip === true
          ) {
            group.tag =
              "vip";
          }

          group.blacklistReason =
            clean(
              profile.blacklistReason
            ) ||
            group.blacklistReason;

          return;
        }

        const key =
          profileId
            ? `PROFILE_${profileId}`
            : phoneNumber
            ? `PHONE_${phoneNumber}`
            : idNumber
            ? `ID_${idNumber}`
            : `PROFILE_ONLY_${
                profile.id ||
                Math.random()
                  .toString(36)
                  .slice(2)
              }`;

        groups[key] = {
          key,
          profileId,
          guestName:
            clean(profile.name) ||
            "Guest",
          phoneNumber,
          address:
            clean(profile.address),
          age:
            profile.age || "",
          gender:
            clean(profile.gender),
          nationality:
            clean(
              profile.nationality
            ) || "Indian",
          idType:
            clean(profile.idType),
          idNumber,
          idFrontUrl:
            clean(
              profile.idFrontUrl
            ),
          idBackUrl:
            clean(
              profile.idBackUrl
            ),
          tag:
            profile.isBlacklisted ===
            true
              ? "blacklist"
              : profile.isVip ===
                true
              ? "vip"
              : "normal",
          blacklistReason:
            clean(
              profile.blacklistReason
            ),
          stays: [],
          totalSpend: 0,
          totalPaid: 0,
          pending: 0,
          totalDiscount: 0,
          totalFood: 0,
          totalLaundry: 0,
          totalExtra: 0,
          lastStayAt:
            profile.lastVisitAt ||
            profile.updatedAt ||
            profile.createdAt ||
            "",
          lastStayTimestamp:
            getTimestamp(
              profile.lastVisitAt ||
                profile.updatedAt ||
                profile.createdAt
            ),
          firstStayAt:
            profile.firstVisitAt ||
            profile.createdAt ||
            "",
          firstStayTimestamp:
            getTimestamp(
              profile.firstVisitAt ||
                profile.createdAt
            ),
          lastRoom: "",
          currentStayCount: 0,
          completedStayCount: 0,
        };
      }
    );

    return Object.values(groups).map(
      (group) => ({
        ...group,

        stays: [...group.stays].sort(
          (firstStay, secondStay) =>
            getTimestamp(
              secondStay.checkInAt
            ) -
            getTimestamp(
              firstStay.checkInAt
            )
        ),

        visitCount: group.stays.length,

        averageSpend:
          group.stays.length > 0
            ? roundMoney(
                group.totalSpend /
                  group.stays.length
              )
            : 0,

        searchText:
          buildGuestSearchText(group),
      })
    );
  }, [stays, guestProfiles]);

  /* ------------------------------------------------------------------------ */
  /*                             Overall Summary                               */
  /* ------------------------------------------------------------------------ */

  const crmSummary = useMemo(
    () => ({
      totalGuests:
        guestGroups.length,

      totalVisits:
        guestGroups.reduce(
          (total, guest) =>
            total +
            guest.visitCount,
          0
        ),

      vipGuests:
        guestGroups.filter(
          (guest) =>
            guest.tag === "vip"
        ).length,

      blacklistedGuests:
        guestGroups.filter(
          (guest) =>
            guest.tag ===
            "blacklist"
        ).length,

      pendingGuests:
        guestGroups.filter(
          (guest) =>
            guest.pending > 0
        ).length,

      lifetimeRevenue:
        roundMoney(
          guestGroups.reduce(
            (total, guest) =>
              total +
              guest.totalSpend,
            0
          )
        ),

      totalPending:
        roundMoney(
          guestGroups.reduce(
            (total, guest) =>
              total +
              guest.pending,
            0
          )
        ),
    }),
    [guestGroups]
  );

  /* ------------------------------------------------------------------------ */
  /*                           Filter and Sorting                              */
  /* ------------------------------------------------------------------------ */

  const filteredGuests = useMemo(() => {
    const searchTerm =
      lower(search);

    const filtered =
      guestGroups.filter((guest) => {
        const matchesSearch =
          !searchTerm ||
          guest.searchText.includes(
            searchTerm
          );

        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter ===
            "pending" &&
            guest.pending > 0) ||
          (statusFilter === "vip" &&
            guest.tag === "vip") ||
          (statusFilter ===
            "blacklist" &&
            guest.tag ===
              "blacklist") ||
          (statusFilter ===
            "repeat" &&
            guest.visitCount > 1) ||
          (statusFilter ===
            "current" &&
            guest.currentStayCount > 0);

        return (
          matchesSearch &&
          matchesStatus
        );
      });

    return [...filtered].sort(
      (firstGuest, secondGuest) => {
        if (sortBy === "spend") {
          return (
            secondGuest.totalSpend -
            firstGuest.totalSpend
          );
        }

        if (sortBy === "visits") {
          return (
            secondGuest.visitCount -
            firstGuest.visitCount
          );
        }

        if (sortBy === "pending") {
          return (
            secondGuest.pending -
            firstGuest.pending
          );
        }

        if (sortBy === "name") {
          return firstGuest.guestName.localeCompare(
            secondGuest.guestName
          );
        }

        return (
          secondGuest.lastStayTimestamp -
          firstGuest.lastStayTimestamp
        );
      }
    );
  }, [
    guestGroups,
    search,
    statusFilter,
    sortBy,
  ]);

  const selectedGuest =
    guestGroups.find(
      (guest) =>
        guest.key ===
        selectedGuestKey
    ) || null;

  /* ------------------------------------------------------------------------ */
  /*                            Update Guest Tag                               */
  /* ------------------------------------------------------------------------ */

  const changeGuestTag = async (
    guest,
    guestTag
  ) => {
    if (
      (!guest?.stays?.length && !guest?.profileId) ||
      savingGuestKey
    ) {
      return;
    }

    let blacklistReason = "";

    if (guestTag === "blacklist") {
      blacklistReason =
        window.prompt(
          "Guest blacklist karne ka reason enter karo:",
          guest.blacklistReason || ""
        );

      if (blacklistReason === null) {
        return;
      }

      if (!clean(blacklistReason)) {
        alert(
          "Blacklist reason required hai."
        );

        return;
      }
    }

    const confirmed =
      window.confirm(
        `${guest.guestName} ko ${
          guestTag === "vip"
            ? "VIP"
            : guestTag ===
              "blacklist"
            ? "Blacklist"
            : "Normal"
        } mark karna hai?`
      );

    if (!confirmed) return;

    setSavingGuestKey(guest.key);

    try {
      const batch = writeBatch(db);

      guest.stays.forEach((stay) => {
        batch.update(
          doc(
            db,
            "hotelStays",
            stay.id
          ),
          {
            guestTag,

            blacklistReason:
              guestTag ===
              "blacklist"
                ? clean(
                    blacklistReason
                  )
                : "",

            guestTagUpdatedAt:
              new Date().toISOString(),
          }
        );
      });

      if (guest.profileId) {
        batch.update(
          doc(
            db,
            "guestProfiles",
            guest.profileId
          ),
          {
            guestTag,
            isVip:
              guestTag === "vip",

            isBlacklisted:
              guestTag ===
              "blacklist",

            blacklistReason:
              guestTag ===
              "blacklist"
                ? clean(
                    blacklistReason
                  )
                : "",

            updatedAt:
              new Date().toISOString(),
          }
        );
      }

      await batch.commit();

      alert(
        "Guest CRM status update ho gaya ✅"
      );
    } catch (error) {
      console.error(
        "Guest tag update error:",
        error
      );

      alert(
        "Guest tag update nahi hua: " +
          (error?.message ||
            error)
      );
    } finally {
      setSavingGuestKey("");
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                       Edit / Add Guest in CRM                             */
  /* ------------------------------------------------------------------------ */

  const openEditGuestForm = (
    guest
  ) => {
    if (!guest) return;

    setEditGuestForm({
      name:
        guest.guestName || "",
      phoneNumber:
        guest.phoneNumber || "",
      age:
        guest.age || "",
      gender:
        guest.gender || "",
      nationality:
        guest.nationality ||
        "Indian",
      address:
        guest.address || "",
      idType:
        guest.idType || "",
      idNumber:
        guest.idNumber || "",

      idFrontUrl:
        guest.idFrontUrl || "",
      idBackUrl:
        guest.idBackUrl || "",
      idFrontFile: null,
      idBackFile: null,
    });

    setEditGuestOpen(true);
    setAddGuestOpen(false);
  };

  const getEditIdPreview = (
    file,
    savedUrl
  ) => {
    if (file) {
      try {
        return URL.createObjectURL(file);
      } catch {
        return "";
      }
    }

    return clean(savedUrl);
  };

  const saveEditedGuest =
    async () => {
      if (
        !selectedGuest ||
        guestFormSaving
      ) {
        return;
      }

      const name =
        clean(
          editGuestForm.name
        );

      const phoneNumber =
        normalizePhone(
          editGuestForm.phoneNumber
        );

      if (!name) {
        alert(
          "Guest name required hai."
        );
        return;
      }

      if (
        phoneNumber &&
        phoneNumber.length < 10
      ) {
        alert(
          "Valid phone number enter karo."
        );
        return;
      }

      setGuestFormSaving(true);

      try {
        let idFrontUrl =
          clean(
            editGuestForm.idFrontUrl ||
              selectedGuest.idFrontUrl
          );

        let idBackUrl =
          clean(
            editGuestForm.idBackUrl ||
              selectedGuest.idBackUrl
          );

        /*
         * CRM se ID replace/re-upload.
         * Same guest profile par new file upload hoti hai aur URL update hota hai.
         */
        if (
          editGuestForm.idFrontFile ||
          editGuestForm.idBackFile
        ) {
          if (!selectedGuest.profileId) {
            throw new Error(
              "Guest profile ID missing hai. ID image replace nahi ho sakti."
            );
          }

          if (editGuestForm.idFrontFile) {
            idFrontUrl =
              await uploadGuestFile({
                hotelId,
                profileId:
                  selectedGuest.profileId,
                file:
                  editGuestForm.idFrontFile,
                side:
                  "id_front",
              });
          }

          if (editGuestForm.idBackFile) {
            idBackUrl =
              await uploadGuestFile({
                hotelId,
                profileId:
                  selectedGuest.profileId,
                file:
                  editGuestForm.idBackFile,
                side:
                  "id_back",
              });
          }
        }

        const batch =
          writeBatch(db);

        const profilePayload = {
          name,
          nameLower:
            lower(name),
          phoneNumber,
          age:
            Number(
              editGuestForm.age ||
                0
            ),
          gender:
            clean(
              editGuestForm.gender
            ),
          nationality:
            clean(
              editGuestForm.nationality
            ) || "Indian",
          address:
            clean(
              editGuestForm.address
            ),
          idType:
            clean(
              editGuestForm.idType
            ),
          idNumber:
            normalizeIdNumber(
              editGuestForm.idNumber
            ),
          idNumberLower:
            lower(
              editGuestForm.idNumber
            ),

          idFrontUrl,
          idBackUrl,

          updatedAt:
            new Date().toISOString(),
        };

        if (
          selectedGuest.profileId
        ) {
          batch.update(
            doc(
              db,
              "guestProfiles",
              selectedGuest.profileId
            ),
            profilePayload
          );
        }

        await batch.commit(); // Profile is canonical; historical registration cards remain snapshots.
        for (const listedStay of selectedGuest.stays || []) {
          await runTransaction(db, async transaction => {
            const reference = doc(db, 'hotelStays', listedStay.id);
            const snapshot = await transaction.get(reference);
            if (!snapshot.exists()) return;
            const stay = snapshot.data();
            if (stay.status !== 'checked_in' || stay.checkedOut) return;
            const guests = Array.isArray(stay.guests) ? stay.guests : [];
            const updatedGuests = guests.map(guest => clean(guest.profileId) === clean(selectedGuest.profileId) ? {...guest, ...profilePayload, isPrimary: guest.isPrimary === true} : guest);
            const primary = updatedGuests.find(guest => guest.isPrimary) || updatedGuests[0];
            const primaryMatches = primary && clean(primary.profileId) === clean(selectedGuest.profileId);
            transaction.update(reference, {guests: updatedGuests, ...(primaryMatches ? {
              guestName: name, phoneNumber, age: profilePayload.age, gender: profilePayload.gender,
              nationality: profilePayload.nationality, address: profilePayload.address,
              idType: profilePayload.idType, idNumber: profilePayload.idNumber, idFrontUrl, idBackUrl} : {}), updatedAt: new Date().toISOString()});
          });
        }


        setEditGuestOpen(false);

        setEditGuestForm(
          (current) => ({
            ...current,
            idFrontUrl,
            idBackUrl,
            idFrontFile: null,
            idBackFile: null,
          })
        );

        alert(
          "Guest details aur ID images update ho gayi ✅"
        );
      } catch (error) {
        console.error(
          "Guest edit error:",
          error
        );

        alert(
          "Guest edit nahi hua: " +
            (error?.message ||
              error)
        );
      } finally {
        setGuestFormSaving(false);
      }
    };

  const resetStandaloneGuestForm =
    () => {
      setAddGuestForm({
        profileId: "",
        searchTerm: "",
        name: "",
        phoneNumber: "",
        age: "",
        gender: "",
        nationality: "Indian",
        address: "",
        relation: "Primary",
        idType: "Aadhaar Card",
        idNumber: "",
        idFrontFile: null,
        idBackFile: null,
        idFrontUrl: "",
        idBackUrl: "",
        sameAddressAsPrimary: false,
        visitCount: 0,
        isVip: false,
        isBlacklisted: false,
        blacklistReason: "",
        isPrimary: true,
      });
    };

  const openAddGuestForm =
    () => {
      resetStandaloneGuestForm();
      setAddGuestOpen(true);
      setEditGuestOpen(false);
    };

  const updateStandaloneGuest = (
    index,
    field,
    value
  ) => {
    setAddGuestForm(
      (current) => ({
        ...current,
        [field]: value,
      })
    );
  };

  const selectStandaloneProfile =
    (index, profile) => {
      if (!profile) return;

      setAddGuestForm({
        profileId:
          profile.profileId ||
          profile.id ||
          "",
        searchTerm:
          profile.name ||
          "",
        name:
          profile.name ||
          "",
        phoneNumber:
          profile.phoneNumber ||
          "",
        age:
          profile.age || "",
        gender:
          profile.gender || "",
        nationality:
          profile.nationality ||
          "Indian",
        address:
          profile.address || "",
        relation:
          profile.relation ||
          "Primary",
        idType:
          profile.idType ||
          "Aadhaar Card",
        idNumber:
          profile.idNumber ||
          "",
        idFrontFile: null,
        idBackFile: null,
        idFrontUrl:
          profile.idFrontUrl ||
          "",
        idBackUrl:
          profile.idBackUrl ||
          "",
        sameAddressAsPrimary:
          false,
        visitCount:
          profile.visitCount ||
          0,
        isVip:
          profile.isVip ===
          true,
        isBlacklisted:
          profile.isBlacklisted ===
          true,
        blacklistReason:
          profile.blacklistReason ||
          "",
        isPrimary: true,
      });
    };

  const printStandaloneGuestIds =
    () => {
      const guest =
        addGuestForm;

      const temporaryUrls = [];

      const getPreview = (
        file,
        savedUrl
      ) => {
        if (file) {
          const url =
            URL.createObjectURL(
              file
            );

          temporaryUrls.push(
            url
          );

          return url;
        }

        return clean(savedUrl);
      };

      const front =
        getPreview(
          guest.idFrontFile,
          guest.idFrontUrl
        );

      const back =
        getPreview(
          guest.idBackFile,
          guest.idBackUrl
        );

      if (!front && !back) {
        alert(
          "Pehle ID Front ya Back image add karo."
        );
        return;
      }

      const printWindow =
        window.open(
          "",
          "_blank",
          "width=1050,height=820"
        );

      if (!printWindow) {
        alert(
          "Browser popup block kar raha hai. Popups allow karo."
        );
        return;
      }

      const escapeHtml = (
        value
      ) =>
        clean(value)
          .replace(
            /&/g,
            "&amp;"
          )
          .replace(
            /</g,
            "&lt;"
          )
          .replace(
            />/g,
            "&gt;"
          )
          .replace(
            /"/g,
            "&quot;"
          )
          .replace(
            /'/g,
            "&#039;"
          );

      const imageBox = (
        title,
        url
      ) => `
        <div class="id-box">
          <div class="id-title">
            ${title}
          </div>

          ${
            url
              ? `<img src="${escapeHtml(
                  url
                )}" alt="${title}" />`
              : `<div class="missing">No ID image</div>`
          }
        </div>
      `;

      printWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <title>
              Guest ID - ${escapeHtml(
                guest.name ||
                  "Guest"
              )}
            </title>

            <style>
              * { box-sizing: border-box; }

              body {
                margin: 0;
                padding: 18px;
                font-family: Arial, sans-serif;
                color: #111827;
              }

              .header {
                text-align: center;
                margin-bottom: 14px;
              }

              .hotel {
                font-size: 22px;
                font-weight: 900;
              }

              .guest-name {
                margin-top: 5px;
                font-size: 15px;
                font-weight: 800;
              }

              .meta {
                display: flex;
                justify-content: center;
                gap: 18px;
                flex-wrap: wrap;
                margin-top: 6px;
                font-size: 11px;
              }

              .ids {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 12px;
                margin-top: 15px;
              }

              .id-box {
                border: 1.5px solid #334155;
                min-height: 290px;
                padding: 10px;
              }

              .id-title {
                margin-bottom: 7px;
                font-size: 13px;
                font-weight: 900;
              }

              .id-box img {
                width: 100%;
                height: 245px;
                object-fit: contain;
                display: block;
              }

              .missing {
                height: 245px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #94a3b8;
              }

              .signature {
                margin-top: 30px;
                font-size: 12px;
                font-weight: 700;
              }

              @page {
                size: A4 portrait;
                margin: 10mm;
              }
            </style>
          </head>

          <body>
            <div class="header">
              <div class="hotel">
                ${escapeHtml(
                  hotelName ||
                    hotelId ||
                    "Hotel"
                )}
              </div>

              <div class="guest-name">
                ${escapeHtml(
                  guest.name ||
                    "Guest"
                )}
              </div>

              <div class="meta">
                <span>
                  Phone:
                  ${escapeHtml(
                    guest.phoneNumber ||
                      "-"
                  )}
                </span>

                <span>
                  ID:
                  ${escapeHtml(
                    guest.idType ||
                      "-"
                  )}
                </span>

                <span>
                  ID No:
                  ${escapeHtml(
                    guest.idNumber ||
                      "-"
                  )}
                </span>
              </div>
            </div>

            <div class="ids">
              ${imageBox(
                "ID Front",
                front
              )}

              ${imageBox(
                "ID Back",
                back
              )}
            </div>

            <div class="signature">
              Guest Signature:
              ______________________________
            </div>

            <script>
              const images =
                Array.from(
                  document.images
                );

              Promise.all(
                images.map(
                  (img) =>
                    img.complete
                      ? Promise.resolve()
                      : new Promise(
                          (resolve) => {
                            img.onload =
                              resolve;
                            img.onerror =
                              resolve;
                          }
                        )
                )
              ).then(() => {
                setTimeout(
                  () =>
                    window.print(),
                  250
                );
              });
            </script>
          </body>
        </html>
      `);

      printWindow.document.close();

      setTimeout(() => {
        temporaryUrls.forEach(
          (url) => {
            try {
              URL.revokeObjectURL(
                url
              );
            } catch {
              // ignore
            }
          }
        );
      }, 60000);
    };

  const saveStandaloneGuest =
    async () => {
      if (guestFormSaving) {
        return;
      }

      if (
        !clean(
          addGuestForm.name
        )
      ) {
        alert(
          "Guest name required hai."
        );
        return;
      }

      const phone =
        normalizePhone(
          addGuestForm.phoneNumber
        );

      if (
        phone &&
        phone.length < 10
      ) {
        alert(
          "Valid phone number enter karo."
        );
        return;
      }

      setGuestFormSaving(
        true
      );

      try {
        const saved =
          await saveGuestProfiles({
            hotelId,
            hotelName,
            guests: [
              {
                ...addGuestForm,
                isPrimary: true,
              },
            ],
          });

        const savedGuest =
          saved?.[0];

        /*
         * Standalone CRM add = visit/check-in nahi.
         * Isliye naya profile create hone ke baad visitCount zero rakhenge.
         */
        if (
          savedGuest?.profileId &&
          !addGuestForm.profileId
        ) {
          await runTransaction(db, async transaction => {
            const reference = doc(db, 'guestProfiles', savedGuest.profileId);
            const snapshot = await transaction.get(reference);
            // A concurrent real check-in owns visit counts; CRM must not reset it.
            if (snapshot.exists() && Number(snapshot.data().visitCount || 0) === 0)
              transaction.update(reference, {standaloneProfile: true, updatedAt: new Date().toISOString()});
          });
        }

        alert(
          "Guest CRM me add ho gaya ✅"
        );

        setAddGuestOpen(false);
        resetStandaloneGuestForm();
      } catch (error) {
        console.error(
          "Standalone guest save error:",
          error
        );

        alert(
          "Guest save nahi hua: " +
            (error?.message ||
              error)
        );
      } finally {
        setGuestFormSaving(
          false
        );
      }
    };

  const openGuestProfileIds = (guest) => {
    if (!guest) return;

    const idFrontUrl = clean(guest.idFrontUrl);
    const idBackUrl = clean(guest.idBackUrl);

    if (!idFrontUrl && !idBackUrl) {
      alert("Is guest ke saved ID images available nahi hain.");
      return;
    }

    setSelectedRegistrationStay({
      id: `PROFILE_IDS_${guest.profileId || guest.key || Date.now()}`,
      profileOnly: true,
      room: "",
      hotelId,
      hotelName: hotelName || "",
      guestName: guest.guestName || "Guest",
      phoneNumber: guest.phoneNumber || "",
      adults: 1,
      children: 0,
      guests: [{
        profileId: guest.profileId || "",
        isPrimary: true,
        relation: "Primary",
        name: guest.guestName || "Guest",
        phoneNumber: guest.phoneNumber || "",
        age: guest.age || "",
        gender: guest.gender || "",
        nationality: guest.nationality || "Indian",
        address: guest.address || "",
        idType: guest.idType || "",
        idNumber: guest.idNumber || "",
        idFrontUrl,
        idBackUrl,
      }],
      idType: guest.idType || "",
      idNumber: guest.idNumber || "",
      idFrontUrl,
      idBackUrl,
      tariff: 0,
      advance: 0,
      paidTotal: 0,
      paymentHistory: [],
      payments: [],
    });
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <section style={sectionStyle}>
      <div style={titleRowStyle}>
        <div>
          <h3 style={titleStyle}>
            Guest History & CRM
          </h3>

          <p style={subtitleStyle}>
            {hotelName || hotelId} ke
            returning guests, previous
            stays aur payment records
          </p>
        </div>

        <div style={titleRightActionsStyle}>
          <button
            type="button"
            onClick={
              openAddGuestForm
            }
            style={globalAddGuestButtonStyle}
          >
            ＋ Add Guest
          </button>

          <div style={countBadgeStyle}>
            {crmSummary.totalGuests} Guests
          </div>
        </div>
      </div>

      <div style={summaryGridStyle}>
        <SummaryCard
          label="Total Guests"
          value={crmSummary.totalGuests}
        />

        <SummaryCard
          label="Total Visits"
          value={crmSummary.totalVisits}
        />

        <SummaryCard
          label="VIP Guests"
          value={crmSummary.vipGuests}
        />

        <SummaryCard
          label="Pending Guests"
          value={crmSummary.pendingGuests}
          danger={
            crmSummary.pendingGuests > 0
          }
        />

        <SummaryCard
          label="Lifetime Revenue"
          value={formatMoney(
            crmSummary.lifetimeRevenue
          )}
        />

        <SummaryCard
          label="Total Pending"
          value={formatMoney(
            crmSummary.totalPending
          )}
          danger={
            crmSummary.totalPending > 0
          }
        />
      </div>

      <div style={toolbarStyle}>
        <input
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
          placeholder="Name, phone, ID, room ya A&D serial search karo"
          style={inputStyle}
        />

        <select
          value={statusFilter}
          onChange={(event) =>
            setStatusFilter(
              event.target.value
            )
          }
          style={selectStyle}
        >
          <option value="all">
            All Guests
          </option>

          <option value="repeat">
            Repeat Guests
          </option>

          <option value="current">
            Currently Staying
          </option>

          <option value="pending">
            Pending Balance
          </option>

          <option value="vip">
            VIP Guests
          </option>

          <option value="blacklist">
            Blacklisted
          </option>
        </select>

        <select
          value={sortBy}
          onChange={(event) =>
            setSortBy(
              event.target.value
            )
          }
          style={selectStyle}
        >
          <option value="recent">
            Recent Visit First
          </option>

          <option value="visits">
            Most Visits
          </option>

          <option value="spend">
            Highest Spend
          </option>

          <option value="pending">
            Highest Pending
          </option>

          <option value="name">
            Name A–Z
          </option>
        </select>
      </div>

      {loadError && (
        <div style={errorBoxStyle}>
          {loadError}
        </div>
      )}

      {loading ? (
        <div style={loadingBoxStyle}>
          Guest history loading...
        </div>
      ) : filteredGuests.length === 0 ? (
        <div style={emptyBoxStyle}>
          Is filter ya search ke liye
          guest history nahi mili.
        </div>
      ) : (
        <div style={guestGridStyle}>
          {filteredGuests.map(
            (guest) => (
              <button
                key={guest.key}
                type="button"
                onClick={() =>
                  setSelectedGuestKey(
                    guest.key
                  )
                }
                style={{
                  ...guestCardStyle,

                  borderColor:
                    guest.tag ===
                    "blacklist"
                      ? "#fecaca"
                      : guest.tag ===
                        "vip"
                      ? "#fde68a"
                      : "#e2e8f0",

                  background:
                    guest.tag ===
                    "blacklist"
                      ? "#fff7f7"
                      : guest.tag ===
                        "vip"
                      ? "#fffdf5"
                      : "#ffffff",
                }}
              >
                <div style={guestCardTopStyle}>
                  <div style={identityStyle}>
                    <div style={avatarStyle}>
                      {guest.guestName
                        .charAt(0)
                        .toUpperCase() ||
                        "G"}
                    </div>

                    <div>
                      <div style={guestNameStyle}>
                        {guest.guestName}
                      </div>

                      <div style={phoneStyle}>
                        {guest.phoneNumber
                          ? `📞 ${guest.phoneNumber}`
                          : "Phone not available"}
                      </div>

                      {guest.idNumber && (
                        <div style={idTextStyle}>
                          🪪{" "}
                          {guest.idType ||
                            "ID"}{" "}
                          •{" "}
                          {maskIdNumber(
                            guest.idNumber
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <TagBadge
                    tag={guest.tag}
                  />
                </div>

                <div style={miniStatsStyle}>
                  <MiniStat
                    label="Visits"
                    value={
                      guest.visitCount
                    }
                  />

                  <MiniStat
                    label="Spend"
                    value={formatMoney(
                      guest.totalSpend
                    )}
                  />

                  <MiniStat
                    label="Pending"
                    value={formatMoney(
                      guest.pending
                    )}
                    danger={
                      guest.pending > 0
                    }
                  />

                  <MiniStat
                    label="Last Room"
                    value={
                      guest.lastRoom ||
                      "-"
                    }
                  />
                </div>

                <div style={lastStayStyle}>
                  Last Stay:{" "}
                  {formatDateTime(
                    guest.lastStayAt
                  )}
                </div>
              </button>
            )
          )}
        </div>
      )}

      {selectedGuest && (
        <div
          style={overlayStyle}
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              setSelectedGuestKey("");
            }
          }}
        >
          <div style={modalStyle}>
            <div style={modalHeaderStyle}>
              <div style={identityStyle}>
                <div style={largeAvatarStyle}>
                  {selectedGuest.guestName
                    .charAt(0)
                    .toUpperCase() ||
                    "G"}
                </div>

                <div>
                  <div style={modalNameRowStyle}>
                    <h2 style={modalTitleStyle}>
                      {
                        selectedGuest.guestName
                      }
                    </h2>

                    <TagBadge
                      tag={
                        selectedGuest.tag
                      }
                    />
                  </div>

                  <div style={phoneStyle}>
                    {selectedGuest.phoneNumber ||
                      "Phone not available"}
                  </div>
                </div>
              </div>

              <div style={modalHeaderActionsStyle}>
                <button
                  type="button"
                  onClick={() => openGuestProfileIds(selectedGuest)}
                  disabled={!clean(selectedGuest.idFrontUrl) && !clean(selectedGuest.idBackUrl)}
                  style={{
                    ...editGuestButtonStyle,
                    background: clean(selectedGuest.idFrontUrl) || clean(selectedGuest.idBackUrl) ? "#0f766e" : "#94a3b8",
                    cursor: clean(selectedGuest.idFrontUrl) || clean(selectedGuest.idBackUrl) ? "pointer" : "not-allowed",
                  }}
                  title="Saved ID documents dekho aur Print IDs Only use karo"
                >
                  🪪 IDs / Print
                </button>

                <button
                  type="button"
                  onClick={() =>
                    openEditGuestForm(
                      selectedGuest
                    )
                  }
                  style={editGuestButtonStyle}
                >
                  ✏ Edit Guest
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setSelectedGuestKey("")
                  }
                  style={closeButtonStyle}
                >
                  ×
                </button>
              </div>
            </div>

            {selectedGuest.tag ===
              "blacklist" && (
              <div style={blacklistWarningStyle}>
                <strong>
                  🚫 Blacklisted Guest
                </strong>

                <div style={{ marginTop: 4 }}>
                  {selectedGuest.blacklistReason ||
                    "Blacklist reason available nahi hai."}
                </div>
              </div>
            )}

            <div style={modalSummaryGridStyle}>
              <SummaryCard
                label="Total Stays"
                value={
                  selectedGuest.visitCount
                }
              />

              <SummaryCard
                label="Lifetime Spend"
                value={formatMoney(
                  selectedGuest.totalSpend
                )}
              />

              <SummaryCard
                label="Total Paid"
                value={formatMoney(
                  selectedGuest.totalPaid
                )}
              />

              <SummaryCard
                label="Pending"
                value={formatMoney(
                  selectedGuest.pending
                )}
                danger={
                  selectedGuest.pending >
                  0
                }
              />

              <SummaryCard
                label="Average Spend"
                value={formatMoney(
                  selectedGuest.averageSpend
                )}
              />

              <SummaryCard
                label="Current Stays"
                value={
                  selectedGuest.currentStayCount
                }
              />
            </div>

            <div style={profileBoxStyle}>
              <ProfileField
                label="Phone"
                value={
                  selectedGuest.phoneNumber ||
                  "-"
                }
              />

              <ProfileField
                label="Age / Gender"
                value={`${selectedGuest.age || "-"} / ${
                  selectedGuest.gender ||
                  "-"
                }`}
              />

              <ProfileField
                label="Nationality"
                value={
                  selectedGuest.nationality ||
                  "-"
                }
              />

              <ProfileField
                label="ID"
                value={`${
                  selectedGuest.idType ||
                  "ID"
                } • ${maskIdNumber(
                  selectedGuest.idNumber
                )}`}
              />

              <ProfileField
                label="First Visit"
                value={formatDateTime(
                  selectedGuest.firstStayAt
                )}
              />

              <ProfileField
                label="Last Visit"
                value={formatDateTime(
                  selectedGuest.lastStayAt
                )}
              />

              <ProfileField
                label="Address"
                value={
                  selectedGuest.address ||
                  "-"
                }
                fullWidth
              />
            </div>

            <div style={{ marginTop: 16, padding: 14, border: "1px solid #dbe3ee", borderRadius: 14, background: "#f8fafc" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                <div>
                  <strong style={{ display: "block", fontSize: 16, color: "#0f172a" }}>🪪 Saved ID Documents</strong>
                  <span style={{ display: "block", marginTop: 3, fontSize: 11, color: "#64748b" }}>Ye guest profile ke permanent saved ID documents hain.</span>
                </div>

                <button
                  type="button"
                  onClick={() => openGuestProfileIds(selectedGuest)}
                  disabled={!clean(selectedGuest.idFrontUrl) && !clean(selectedGuest.idBackUrl)}
                  style={{
                    ...smallActionButtonStyle(clean(selectedGuest.idFrontUrl) || clean(selectedGuest.idBackUrl) ? "#0f766e" : "#94a3b8"),
                    cursor: clean(selectedGuest.idFrontUrl) || clean(selectedGuest.idBackUrl) ? "pointer" : "not-allowed",
                    opacity: clean(selectedGuest.idFrontUrl) || clean(selectedGuest.idBackUrl) ? 1 : 0.7,
                  }}
                >
                  🖨 View / Print IDs
                </button>
              </div>

              {clean(selectedGuest.idFrontUrl) || clean(selectedGuest.idBackUrl) ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
                  <div style={{ padding: 10, border: "1px solid #dbe3ee", borderRadius: 12, background: "#ffffff" }}>
                    <div style={{ marginBottom: 7, fontSize: 12, fontWeight: 900, color: "#334155" }}>ID Front</div>
                    {clean(selectedGuest.idFrontUrl) ? (
                      <img src={selectedGuest.idFrontUrl} alt="ID Front" style={{ display: "block", width: "100%", height: 190, objectFit: "contain", borderRadius: 8, background: "#f8fafc" }} />
                    ) : (
                      <div style={{ minHeight: 110, display: "grid", placeItems: "center", color: "#94a3b8", fontSize: 12 }}>ID Front not available</div>
                    )}
                  </div>

                  <div style={{ padding: 10, border: "1px solid #dbe3ee", borderRadius: 12, background: "#ffffff" }}>
                    <div style={{ marginBottom: 7, fontSize: 12, fontWeight: 900, color: "#334155" }}>ID Back</div>
                    {clean(selectedGuest.idBackUrl) ? (
                      <img src={selectedGuest.idBackUrl} alt="ID Back" style={{ display: "block", width: "100%", height: 190, objectFit: "contain", borderRadius: 8, background: "#f8fafc" }} />
                    ) : (
                      <div style={{ minHeight: 110, display: "grid", placeItems: "center", color: "#94a3b8", fontSize: 12 }}>ID Back not available</div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ padding: 14, border: "1px dashed #cbd5e1", borderRadius: 10, textAlign: "center", color: "#64748b", fontSize: 12 }}>
                  Is guest profile me abhi saved ID images available nahi hain.
                </div>
              )}
            </div>

            <div style={tagActionsStyle}>
              <strong>
                Guest CRM Status:
              </strong>

              <button
                type="button"
                disabled={
                  savingGuestKey ===
                  selectedGuest.key
                }
                onClick={() =>
                  changeGuestTag(
                    selectedGuest,
                    "normal"
                  )
                }
                style={tagButtonStyle(
                  "#475569"
                )}
              >
                Normal
              </button>

              <button
                type="button"
                disabled={
                  savingGuestKey ===
                  selectedGuest.key
                }
                onClick={() =>
                  changeGuestTag(
                    selectedGuest,
                    "vip"
                  )
                }
                style={tagButtonStyle(
                  "#7c3aed"
                )}
              >
                VIP ⭐
              </button>

              <button
                type="button"
                disabled={
                  savingGuestKey ===
                  selectedGuest.key
                }
                onClick={() =>
                  changeGuestTag(
                    selectedGuest,
                    "blacklist"
                  )
                }
                style={tagButtonStyle(
                  "#dc2626"
                )}
              >
                Blacklist
              </button>

              {savingGuestKey ===
                selectedGuest.key && (
                <span style={savingTextStyle}>
                  Updating...
                </span>
              )}
            </div>

            {editGuestOpen && (
              <section style={guestEditorStyle}>
                <div style={guestEditorHeaderStyle}>
                  <div>
                    <strong>
                      ✏ Edit Guest Details
                    </strong>

                    <div style={guestEditorSubStyle}>
                      Primary CRM profile aur previous stay snapshots update honge.
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setEditGuestOpen(
                        false
                      )
                    }
                    style={editorCloseButtonStyle}
                  >
                    ×
                  </button>
                </div>

                <GuestEditorFields
                  form={editGuestForm}
                  setForm={
                    setEditGuestForm
                  }
                  disabled={
                    guestFormSaving
                  }
                />

                <div
                  style={{
                    marginTop: 14,
                    padding: 12,
                    border:
                      "1px solid #bfdbfe",
                    borderRadius: 12,
                    background: "#f8fbff",
                  }}
                >
                  <div
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <strong
                      style={{
                        display: "block",
                        color: "#0f172a",
                        fontSize: 14,
                      }}
                    >
                      🪪 Replace / Re-upload ID Images
                    </strong>

                    <div
                      style={{
                        marginTop: 3,
                        color: "#64748b",
                        fontSize: 10,
                        lineHeight: 1.45,
                      }}
                    >
                      Agar purani ID photo tedhi, blur ya galat crop hai to yahin se nayi photo click/upload karo. Save Changes par CRM profile aur linked stay snapshots update ho jayenge.
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {[
                      {
                        side: "Front",
                        fileField:
                          "idFrontFile",
                        urlField:
                          "idFrontUrl",
                      },
                      {
                        side: "Back",
                        fileField:
                          "idBackFile",
                        urlField:
                          "idBackUrl",
                      },
                    ].map((item) => {
                      const preview =
                        getEditIdPreview(
                          editGuestForm[
                            item.fileField
                          ],
                          editGuestForm[
                            item.urlField
                          ]
                        );

                      return (
                        <div
                          key={item.side}
                          style={{
                            padding: 10,
                            border:
                              "1px solid #dbe3ee",
                            borderRadius: 10,
                            background:
                              "#ffffff",
                          }}
                        >
                          <div
                            style={{
                              marginBottom: 7,
                              fontWeight: 900,
                              fontSize: 12,
                              color:
                                "#334155",
                            }}
                          >
                            ID {item.side}
                          </div>

                          <div
                            style={{
                              minHeight: 170,
                              display: "grid",
                              placeItems:
                                "center",
                              overflow:
                                "hidden",
                              borderRadius: 8,
                              background:
                                "#f1f5f9",
                            }}
                          >
                            {preview ? (
                              <img
                                src={preview}
                                alt={`ID ${item.side}`}
                                style={{
                                  width:
                                    "100%",
                                  height: 190,
                                  objectFit:
                                    "contain",
                                  display:
                                    "block",
                                }}
                              />
                            ) : (
                              <span
                                style={{
                                  color:
                                    "#94a3b8",
                                  fontSize:
                                    11,
                                }}
                              >
                                No ID image
                              </span>
                            )}
                          </div>

                          <div
                            style={{
                              display:
                                "grid",
                              gridTemplateColumns:
                                "1fr 1fr",
                              gap: 8,
                              marginTop: 9,
                            }}
                          >
                            <label
                              style={{
                                display:
                                  "block",
                                padding:
                                  "9px 8px",
                                borderRadius:
                                  8,
                                textAlign:
                                  "center",
                                background:
                                  "#2563eb",
                                color:
                                  "#ffffff",
                                fontSize:
                                  10,
                                fontWeight:
                                  900,
                                cursor:
                                  guestFormSaving
                                    ? "not-allowed"
                                    : "pointer",
                                opacity:
                                  guestFormSaving
                                    ? 0.6
                                    : 1,
                              }}
                            >
                              📷 Camera
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                disabled={
                                  guestFormSaving
                                }
                                onChange={(
                                  event
                                ) => {
                                  const file =
                                    event
                                      .target
                                      .files?.[0] ||
                                    null;

                                  if (
                                    file
                                  ) {
                                    setEditGuestForm(
                                      (
                                        current
                                      ) => ({
                                        ...current,
                                        [item.fileField]:
                                          file,
                                      })
                                    );
                                  }

                                  event.target.value =
                                    "";
                                }}
                                style={{
                                  display:
                                    "none",
                                }}
                              />
                            </label>

                            <label
                              style={{
                                display:
                                  "block",
                                padding:
                                  "9px 8px",
                                borderRadius:
                                  8,
                                textAlign:
                                  "center",
                                background:
                                  "#0f766e",
                                color:
                                  "#ffffff",
                                fontSize:
                                  10,
                                fontWeight:
                                  900,
                                cursor:
                                  guestFormSaving
                                    ? "not-allowed"
                                    : "pointer",
                                opacity:
                                  guestFormSaving
                                    ? 0.6
                                    : 1,
                              }}
                            >
                              ⬆ Re-upload
                              <input
                                type="file"
                                accept="image/*"
                                disabled={
                                  guestFormSaving
                                }
                                onChange={(
                                  event
                                ) => {
                                  const file =
                                    event
                                      .target
                                      .files?.[0] ||
                                    null;

                                  if (
                                    file
                                  ) {
                                    setEditGuestForm(
                                      (
                                        current
                                      ) => ({
                                        ...current,
                                        [item.fileField]:
                                          file,
                                      })
                                    );
                                  }

                                  event.target.value =
                                    "";
                                }}
                                style={{
                                  display:
                                    "none",
                                }}
                              />
                            </label>
                          </div>

                          {editGuestForm[
                            item.fileField
                          ] && (
                            <button
                              type="button"
                              disabled={
                                guestFormSaving
                              }
                              onClick={() =>
                                setEditGuestForm(
                                  (
                                    current
                                  ) => ({
                                    ...current,
                                    [item.fileField]:
                                      null,
                                  })
                                )
                              }
                              style={{
                                width:
                                  "100%",
                                marginTop:
                                  7,
                                border:
                                  "1px solid #fecaca",
                                borderRadius:
                                  7,
                                padding:
                                  "7px 8px",
                                background:
                                  "#fff1f2",
                                color:
                                  "#be123c",
                                fontSize:
                                  9,
                                fontWeight:
                                  900,
                                cursor:
                                  "pointer",
                              }}
                            >
                              ↩ Keep Old Image
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={guestEditorActionsStyle}>
                  <button
                    type="button"
                    disabled={
                      guestFormSaving
                    }
                    onClick={
                      saveEditedGuest
                    }
                    style={editorSaveButtonStyle}
                  >
                    {guestFormSaving
                      ? "Saving..."
                      : "✓ Save Changes"}
                  </button>

                  <button
                    type="button"
                    disabled={
                      guestFormSaving
                    }
                    onClick={() =>
                      setEditGuestOpen(
                        false
                      )
                    }
                    style={editorCancelButtonStyle}
                  >
                    Cancel
                  </button>
                </div>
              </section>
            )}

            <h3 style={historyHeadingStyle}>
              Previous Stays
            </h3>

            <div style={historyListStyle}>
              {selectedGuest.stays.length ===
                0 && (
                <div style={noStayRecordStyle}>
                  Abhi is guest ka koi hotel stay/check-in record nahi hai.
                </div>
              )}

              {selectedGuest.stays.map(
                (stay) => {
                  const bill =
                    stay.calculatedBill ||
                    calculateStayBill(stay);

                  return (
                    <div
                      key={stay.id}
                      style={historyCardStyle}
                    >
                      <div style={historyTopStyle}>
                        <div>
                          <strong style={roomTitleStyle}>
                            Room{" "}
                            {stay.room || "-"}
                          </strong>

                          <div style={historyMetaStyle}>
                            A&D Serial:{" "}
                            {stay.adRegisterSerialNo ||
                              "-"}
                          </div>
                        </div>

                        <StatusBadge
                          status={getStayStatus(
                            stay
                          )}
                        />
                      </div>

                      <div style={stayInfoGridStyle}>
                        <ProfileField
                          label="Check-In"
                          value={formatDateTime(
                            stay.checkInAt
                          )}
                        />

                        <ProfileField
                          label="Checkout"
                          value={formatDateTime(
                            getStayCheckoutDate(
                              stay
                            )
                          )}
                        />

                        <ProfileField
                          label="Stay Type"
                          value={
                            stay.stayType ||
                            "-"
                          }
                        />

                        <ProfileField
                          label="Booking Source"
                          value={
                            stay.bookingSource ||
                            "-"
                          }
                        />
                      </div>

                      <div style={billGridStyle}>
                        <BillMini
                          label="Room"
                          value={
                            bill.roomRent
                          }
                        />

                        <BillMini
                          label="Food"
                          value={bill.food}
                        />

                        <BillMini
                          label="Laundry"
                          value={
                            bill.laundry
                          }
                        />

                        <BillMini
                          label="Extra"
                          value={bill.extra}
                        />

                        <BillMini
                          label="Total"
                          value={
                            bill.grandTotal
                          }
                          strong
                        />

                        <BillMini
                          label="Paid"
                          value={bill.paid}
                        />

                        <BillMini
                          label="Balance"
                          value={
                            bill.balance
                          }
                          danger={
                            bill.balance > 0
                          }
                        />
                      </div>

                      {(stay.notes ||
                        stay.discountReason) && (
                        <div style={noteStyle}>
                          {stay.notes
                            ? `Note: ${stay.notes}`
                            : `Discount: ${stay.discountReason}`}
                        </div>
                      )}

                      <div style={historyActionsStyle}>
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedRegistrationStay(
                              stay
                            )
                          }
                          style={smallActionButtonStyle(
                            "#2563eb"
                          )}
                        >
                          🪪 Registration / IDs
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            setSelectedInvoiceStay(
                              stay
                            )
                          }
                          style={smallActionButtonStyle(
                            "#0f766e"
                          )}
                        >
                          🧾 Invoice
                        </button>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </div>
      )}

      {addGuestOpen && (
        <div
          style={overlayStyle}
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget &&
              !guestFormSaving
            ) {
              setAddGuestOpen(
                false
              );
            }
          }}
        >
          <div style={standaloneGuestModalStyle}>
            <div style={modalHeaderStyle}>
              <div>
                <h2 style={modalTitleStyle}>
                  ＋ Add Guest to CRM
                </h2>

                <div style={standaloneGuestHelpStyle}>
                  Ye guest kisi room/stay se linked hona zaroori nahi hai. ID Front/Back add, crop aur print bhi kar sakte ho.
                </div>
              </div>

              <button
                type="button"
                disabled={
                  guestFormSaving
                }
                onClick={() =>
                  setAddGuestOpen(
                    false
                  )
                }
                style={closeButtonStyle}
              >
                ×
              </button>
            </div>

            <GuestDetailsForm
              guest={addGuestForm}
              index={0}
              profiles={
                guestProfiles
              }
              disabled={
                guestFormSaving
              }
              primaryAddress={
                addGuestForm.address ||
                ""
              }
              onChange={
                updateStandaloneGuest
              }
              onSelectProfile={
                selectStandaloneProfile
              }
              onRemove={() => {}}
            />

            <div style={standaloneActionBarStyle}>
              <button
                type="button"
                disabled={
                  guestFormSaving
                }
                onClick={
                  printStandaloneGuestIds
                }
                style={printGuestIdsButtonStyle}
              >
                🪪 Print IDs
              </button>

              <button
                type="button"
                disabled={
                  guestFormSaving
                }
                onClick={
                  saveStandaloneGuest
                }
                style={editorSaveButtonStyle}
              >
                {guestFormSaving
                  ? "Saving..."
                  : "✓ Save Guest"}
              </button>

              <button
                type="button"
                disabled={
                  guestFormSaving
                }
                onClick={() =>
                  setAddGuestOpen(
                    false
                  )
                }
                style={editorCancelButtonStyle}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedRegistrationStay && (
        <RegistrationCardModal
          stay={selectedRegistrationStay}
          hotelName={hotelName}
          title={
            selectedRegistrationStay?.profileOnly
              ? "Guest ID Documents"
              : "Previous Registration Card"
          }
          successMessage={
            selectedRegistrationStay?.profileOnly
              ? "CRM profile ke saved ID documents."
              : "Guest ke previous stay ka registration record aur ID documents."
          }
          onClose={() =>
            setSelectedRegistrationStay(
              null
            )
          }
        />
      )}

      {selectedInvoiceStay && (
        <InvoiceModal
          stay={selectedInvoiceStay}
          bill={calculateStayBill(
            selectedInvoiceStay
          )}
          invoiceNumber={
            selectedInvoiceStay.invoiceNumber ||
            `INV-${selectedInvoiceStay.id}`
          }
          hotelName={hotelName}
          onClose={() =>
            setSelectedInvoiceStay(null)
          }
        />
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Small Components                               */
/* -------------------------------------------------------------------------- */

function GuestEditorFields({
  form,
  setForm,
  disabled = false,
  includeRelation = false,
}) {
  const update = (
    field,
    value
  ) =>
    setForm(
      (current) => ({
        ...current,
        [field]: value,
      })
    );

  return (
    <div style={guestEditorGridStyle}>
      <label>
        <div style={editorLabelStyle}>
          Full Name *
        </div>

        <input
          value={form.name}
          disabled={disabled}
          onChange={(event) =>
            update(
              "name",
              event.target.value
            )
          }
          style={editorInputStyle}
        />
      </label>

      <label>
        <div style={editorLabelStyle}>
          Phone Number
        </div>

        <input
          value={
            form.phoneNumber
          }
          disabled={disabled}
          onChange={(event) =>
            update(
              "phoneNumber",
              event.target.value
            )
          }
          style={editorInputStyle}
          maxLength={15}
        />
      </label>

      <label>
        <div style={editorLabelStyle}>
          Age
        </div>

        <input
          type="number"
          min="0"
          value={form.age}
          disabled={disabled}
          onChange={(event) =>
            update(
              "age",
              event.target.value
            )
          }
          style={editorInputStyle}
        />
      </label>

      <label>
        <div style={editorLabelStyle}>
          Gender
        </div>

        <select
          value={form.gender}
          disabled={disabled}
          onChange={(event) =>
            update(
              "gender",
              event.target.value
            )
          }
          style={editorInputStyle}
        >
          <option value="">
            Select
          </option>
          <option value="Male">
            Male
          </option>
          <option value="Female">
            Female
          </option>
          <option value="Other">
            Other
          </option>
        </select>
      </label>

      <label>
        <div style={editorLabelStyle}>
          Nationality
        </div>

        <input
          value={
            form.nationality
          }
          disabled={disabled}
          onChange={(event) =>
            update(
              "nationality",
              event.target.value
            )
          }
          style={editorInputStyle}
        />
      </label>

      {includeRelation && (
        <label>
          <div style={editorLabelStyle}>
            Relation
          </div>

          <input
            value={
              form.relation || ""
            }
            disabled={disabled}
            onChange={(event) =>
              update(
                "relation",
                event.target.value
              )
            }
            style={editorInputStyle}
            placeholder="Friend / Spouse / Family"
          />
        </label>
      )}

      <label>
        <div style={editorLabelStyle}>
          ID Type
        </div>

        <select
          value={form.idType}
          disabled={disabled}
          onChange={(event) =>
            update(
              "idType",
              event.target.value
            )
          }
          style={editorInputStyle}
        >
          <option value="">
            Select ID
          </option>
          <option value="Aadhaar Card">
            Aadhaar Card
          </option>
          <option value="Driving Licence">
            Driving Licence
          </option>
          <option value="Passport">
            Passport
          </option>
          <option value="Voter ID">
            Voter ID
          </option>
          <option value="Other">
            Other
          </option>
        </select>
      </label>

      <label>
        <div style={editorLabelStyle}>
          ID Number
        </div>

        <input
          value={form.idNumber}
          disabled={disabled}
          onChange={(event) =>
            update(
              "idNumber",
              event.target.value
            )
          }
          style={editorInputStyle}
        />
      </label>

      <label style={editorWideFieldStyle}>
        <div style={editorLabelStyle}>
          Address
        </div>

        <textarea
          value={form.address}
          disabled={disabled}
          onChange={(event) =>
            update(
              "address",
              event.target.value
            )
          }
          style={editorTextareaStyle}
        />
      </label>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  danger = false,
}) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryLabelStyle}>
        {label}
      </div>

      <div
        style={{
          ...summaryValueStyle,

          color: danger
            ? "#dc2626"
            : "#0f172a",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function MiniStat({
  label,
  value,
  danger = false,
}) {
  return (
    <div>
      <div style={miniLabelStyle}>
        {label}
      </div>

      <div
        style={{
          ...miniValueStyle,

          color: danger
            ? "#dc2626"
            : "#0f172a",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function ProfileField({
  label,
  value,
  fullWidth = false,
}) {
  return (
    <div
      style={{
        ...profileFieldStyle,

        gridColumn: fullWidth
          ? "1 / -1"
          : "auto",
      }}
    >
      <span style={profileLabelStyle}>
        {label}
      </span>

      <strong style={profileValueStyle}>
        {value}
      </strong>
    </div>
  );
}

function BillMini({
  label,
  value,
  strong = false,
  danger = false,
}) {
  return (
    <div style={billMiniStyle}>
      <span style={billMiniLabelStyle}>
        {label}
      </span>

      <strong
        style={{
          ...billMiniValueStyle,

          fontSize: strong
            ? 14
            : 12,

          color: danger
            ? "#dc2626"
            : "#0f172a",
        }}
      >
        {formatMoney(value)}
      </strong>
    </div>
  );
}

function TagBadge({ tag }) {
  if (tag === "vip") {
    return (
      <span
        style={{
          ...badgeStyle,
          background: "#f3e8ff",
          color: "#7c3aed",
        }}
      >
        VIP ⭐
      </span>
    );
  }

  if (tag === "blacklist") {
    return (
      <span
        style={{
          ...badgeStyle,
          background: "#fee2e2",
          color: "#dc2626",
        }}
      >
        Blacklisted
      </span>
    );
  }

  return (
    <span
      style={{
        ...badgeStyle,
        background: "#e2e8f0",
        color: "#475569",
      }}
    >
      Normal
    </span>
  );
}

function StatusBadge({ status }) {
  if (status === "checked_out") {
    return (
      <span
        style={{
          ...badgeStyle,
          background: "#dbeafe",
          color: "#1d4ed8",
        }}
      >
        Checked Out
      </span>
    );
  }

  if (status === "cancelled") {
    return (
      <span
        style={{
          ...badgeStyle,
          background: "#e2e8f0",
          color: "#475569",
        }}
      >
        Cancelled
      </span>
    );
  }

  return (
    <span
      style={{
        ...badgeStyle,
        background: "#dcfce7",
        color: "#15803d",
      }}
    >
      Checked In
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const titleRightActionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

const globalAddGuestButtonStyle = {
  border: 0,
  borderRadius: 10,
  padding: "10px 15px",
  background: "#16a34a",
  color: "#ffffff",
  fontSize: 12,
  fontWeight: 900,
  cursor: "pointer",
};

const standaloneGuestModalStyle = {
  width: "min(1050px,100%)",
  maxHeight: "94vh",
  overflowY: "auto",
  padding: 20,
  borderRadius: 20,
  background: "#ffffff",
  boxSizing: "border-box",
};

const standaloneGuestHelpStyle = {
  marginTop: 5,
  color: "#64748b",
  fontSize: 11,
  maxWidth: 720,
};

const standaloneActionBarStyle = {
  position: "sticky",
  bottom: -20,
  zIndex: 8,
  display: "flex",
  justifyContent: "flex-end",
  gap: 9,
  flexWrap: "wrap",
  margin: "15px -20px -20px",
  padding: "13px 20px",
  borderTop: "1px solid #e2e8f0",
  background: "rgba(255,255,255,.98)",
};

const printGuestIdsButtonStyle = {
  border: 0,
  borderRadius: 8,
  padding: "9px 13px",
  background: "#2563eb",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const noStayRecordStyle = {
  padding: 18,
  border: "1px dashed #cbd5e1",
  borderRadius: 10,
  background: "#f8fafc",
  color: "#64748b",
  textAlign: "center",
  fontSize: 11,
};

const modalHeaderActionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

const editGuestButtonStyle = {
  border: 0,
  borderRadius: 9,
  padding: "9px 12px",
  background: "#2563eb",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const addGuestButtonStyle = {
  border: 0,
  borderRadius: 9,
  padding: "9px 12px",
  background: "#16a34a",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const guestEditorStyle = {
  marginTop: 14,
  padding: 14,
  border: "1px solid #bfdbfe",
  borderRadius: 12,
  background: "#eff6ff",
};

const guestEditorHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 10,
  marginBottom: 12,
};

const guestEditorSubStyle = {
  marginTop: 4,
  color: "#64748b",
  fontSize: 10,
};

const editorCloseButtonStyle = {
  width: 30,
  height: 30,
  border: 0,
  borderRadius: 7,
  background: "#e2e8f0",
  color: "#0f172a",
  fontSize: 18,
  fontWeight: 900,
  cursor: "pointer",
};

const guestEditorGridStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(180px,1fr))",
  gap: 10,
};

const editorLabelStyle = {
  marginBottom: 5,
  color: "#334155",
  fontSize: 9,
  fontWeight: 900,
};

const editorInputStyle = {
  width: "100%",
  padding: "9px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  background: "#ffffff",
  color: "#0f172a",
  boxSizing: "border-box",
};

const editorWideFieldStyle = {
  gridColumn: "1 / -1",
};

const editorTextareaStyle = {
  ...editorInputStyle,
  minHeight: 72,
  resize: "vertical",
};

const guestEditorActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  marginTop: 12,
};

const editorSaveButtonStyle = {
  border: 0,
  borderRadius: 8,
  padding: "9px 13px",
  background: "#16a34a",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const editorCancelButtonStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "9px 13px",
  background: "#ffffff",
  color: "#475569",
  fontWeight: 800,
  cursor: "pointer",
};

const tagButtonStyle = (background) => ({
  border: 0,
  borderRadius: 9,
  padding: "8px 11px",

  background,
  color: "#ffffff",

  fontWeight: 800,
  cursor: "pointer",
});

const smallActionButtonStyle = (
  background
) => ({
  border: 0,
  borderRadius: 8,
  padding: "8px 10px",

  background,
  color: "#ffffff",

  fontSize: 11,
  fontWeight: 800,
  cursor: "pointer",
});

const sectionStyle = {
  marginTop: 34,
  padding: 18,

  borderRadius: 18,

  background: "#f8fafc",

  border:
    "1px solid #e2e8f0",
};

const titleRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",

  gap: 12,
  flexWrap: "wrap",
};

const titleStyle = {
  margin: 0,
  color: "#0f172a",
};

const subtitleStyle = {
  margin: "5px 0 0",
  color: "#64748b",
};

const countBadgeStyle = {
  padding: "8px 12px",

  borderRadius: 999,

  background: "#dbeafe",
  color: "#1d4ed8",

  fontWeight: 900,
};

const summaryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(130px,1fr))",

  gap: 9,

  marginTop: 16,
};

const summaryCardStyle = {
  padding: 12,

  borderRadius: 12,

  background: "#ffffff",

  border:
    "1px solid #e2e8f0",
};

const summaryLabelStyle = {
  color: "#64748b",

  fontSize: 10,
  fontWeight: 800,

  textTransform: "uppercase",
};

const summaryValueStyle = {
  marginTop: 5,

  fontSize: 18,
  fontWeight: 900,

  overflowWrap: "anywhere",
};

const toolbarStyle = {
  display: "grid",

  gridTemplateColumns:
    "minmax(240px,1fr) minmax(150px,210px) minmax(150px,210px)",

  gap: 10,

  marginTop: 16,
};

const inputStyle = {
  width: "100%",
  minHeight: 44,

  padding: 11,

  borderRadius: 10,

  border:
    "1px solid #cbd5e1",

  background: "#ffffff",

  boxSizing: "border-box",
};

const selectStyle = {
  ...inputStyle,
  cursor: "pointer",
};

const errorBoxStyle = {
  marginTop: 15,
  padding: 12,

  borderRadius: 11,

  background: "#fef2f2",
  color: "#b91c1c",

  border:
    "1px solid #fecaca",

  fontSize: 12,
  fontWeight: 700,
};

const loadingBoxStyle = {
  marginTop: 15,
  padding: 18,

  borderRadius: 12,

  background: "#ffffff",
  color: "#64748b",

  textAlign: "center",
};

const emptyBoxStyle = {
  ...loadingBoxStyle,
  border:
    "1px dashed #cbd5e1",
};

const guestGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(270px,1fr))",

  gap: 12,

  marginTop: 16,
};

const guestCardStyle = {
  padding: 14,

  border:
    "1px solid #e2e8f0",

  borderRadius: 15,

  textAlign: "left",

  cursor: "pointer",

  boxShadow:
    "0 4px 15px rgba(15,23,42,.04)",
};

const guestCardTopStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 10,
};

const identityStyle = {
  display: "flex",
  alignItems: "center",

  gap: 10,
  minWidth: 0,
};

const avatarStyle = {
  width: 42,
  height: 42,

  flex: "0 0 auto",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  borderRadius: "50%",

  background: "#dbeafe",
  color: "#1d4ed8",

  fontWeight: 900,
};

const largeAvatarStyle = {
  ...avatarStyle,

  width: 52,
  height: 52,

  fontSize: 20,
};

const guestNameStyle = {
  color: "#0f172a",

  fontSize: 16,
  fontWeight: 900,

  overflowWrap: "anywhere",
};

const phoneStyle = {
  marginTop: 4,

  color: "#64748b",

  fontSize: 12,
};

const idTextStyle = {
  marginTop: 3,

  color: "#475569",

  fontSize: 10,
};

const miniStatsStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(4,1fr)",

  gap: 8,

  marginTop: 14,
};

const miniLabelStyle = {
  color: "#64748b",

  fontSize: 9,
  fontWeight: 800,
};

const miniValueStyle = {
  marginTop: 3,

  fontSize: 11,
  fontWeight: 900,

  overflowWrap: "anywhere",
};

const lastStayStyle = {
  marginTop: 12,
  paddingTop: 10,

  borderTop:
    "1px solid #e2e8f0",

  color: "#475569",

  fontSize: 11,
};

const badgeStyle = {
  display: "inline-block",

  padding: "5px 8px",

  borderRadius: 999,

  fontSize: 10,
  fontWeight: 900,
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 15000,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  padding: 12,

  background:
    "rgba(15,23,42,.72)",
};

const modalStyle = {
  width: "min(980px,100%)",
  maxHeight: "94vh",

  overflowY: "auto",

  padding: 20,

  borderRadius: 20,

  background: "#ffffff",

  boxSizing: "border-box",
};

const modalHeaderStyle = {
  position: "sticky",
  top: -20,
  zIndex: 10,

  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 12,

  margin: "-20px -20px 15px",
  padding: "17px 20px",

  background:
    "rgba(255,255,255,.97)",

  borderBottom:
    "1px solid #e2e8f0",
};

const modalNameRowStyle = {
  display: "flex",
  alignItems: "center",

  gap: 8,
  flexWrap: "wrap",
};

const modalTitleStyle = {
  margin: 0,
};

const closeButtonStyle = {
  width: 38,
  height: 38,

  border: 0,
  borderRadius: 10,

  background: "#f1f5f9",

  fontSize: 25,
  cursor: "pointer",
};

const blacklistWarningStyle = {
  marginBottom: 13,
  padding: 11,

  borderRadius: 11,

  background: "#fef2f2",
  color: "#b91c1c",

  border:
    "1px solid #fecaca",

  fontSize: 12,
};

const modalSummaryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(135px,1fr))",

  gap: 9,
};

const profileBoxStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(190px,1fr))",

  gap: 9,

  marginTop: 14,
  padding: 12,

  borderRadius: 13,

  background: "#f8fafc",

  border:
    "1px solid #e2e8f0",
};

const profileFieldStyle = {
  minWidth: 0,
};

const profileLabelStyle = {
  display: "block",

  marginBottom: 4,

  color: "#64748b",

  fontSize: 9,
  fontWeight: 800,

  textTransform: "uppercase",
};

const profileValueStyle = {
  display: "block",

  color: "#334155",

  fontSize: 11,
  fontWeight: 800,

  overflowWrap: "anywhere",
};

const tagActionsStyle = {
  display: "flex",
  alignItems: "center",

  gap: 8,
  flexWrap: "wrap",

  marginTop: 16,
  padding: 12,

  borderRadius: 12,

  background: "#f8fafc",
};

const savingTextStyle = {
  color: "#64748b",
  fontSize: 11,
};

const historyHeadingStyle = {
  margin: "18px 0 10px",
};

const historyListStyle = {
  display: "flex",
  flexDirection: "column",

  gap: 10,
};

const historyCardStyle = {
  padding: 13,

  borderRadius: 13,

  background: "#ffffff",

  border:
    "1px solid #e2e8f0",
};

const historyTopStyle = {
  display: "flex",
  justifyContent: "space-between",

  gap: 10,
};

const roomTitleStyle = {
  color: "#0f172a",
};

const historyMetaStyle = {
  marginTop: 4,

  color: "#64748b",

  fontSize: 10,
};

const stayInfoGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(150px,1fr))",

  gap: 8,

  marginTop: 10,
};

const billGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(90px,1fr))",

  gap: 7,

  marginTop: 10,
  paddingTop: 10,

  borderTop:
    "1px dashed #cbd5e1",
};

const billMiniStyle = {
  padding: 7,

  borderRadius: 8,

  background: "#f8fafc",
};

const billMiniLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 8,
  fontWeight: 800,

  textTransform: "uppercase",
};

const billMiniValueStyle = {
  display: "block",

  marginTop: 3,

  fontWeight: 900,
};

const noteStyle = {
  marginTop: 9,
  padding: 8,

  borderRadius: 8,

  background: "#fff7ed",
  color: "#9a3412",

  fontSize: 11,
};

const historyActionsStyle = {
  display: "flex",

  gap: 8,
  flexWrap: "wrap",

  marginTop: 10,
};

export default GuestHistoryPanel;